import * as yup from 'yup';

import { FormField } from '@common/types/api';

import { RenderableField } from '../types';

const cpfRegex = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
const cnpjRegex = /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;
const cepRegex = /^\d{5}-\d{3}$/;

const REQUIRED_FIELD_MESSAGE = 'Campo obrigatório';

const getBaseSchema = (fieldType: string): yup.AnySchema => {
  const schemaMap: Record<string, () => yup.AnySchema> = {
    email: () => yup.string().email('E-mail inválido'),
    password: () =>
      yup.string().min(6, 'A senha deve ter no mínimo 6 caracteres'),
    number: () => yup.number().typeError('Deve ser um número'),
    cpf: () =>
      yup.string().matches(cpfRegex, 'CPF inválido (formato: 000.000.000-00)'),
    cnpj: () =>
      yup
        .string()
        .matches(cnpjRegex, 'CNPJ inválido (formato: 00.000.000/0000-00)'),
    cep: () =>
      yup.string().matches(cepRegex, 'CEP inválido (formato: 00000-000)'),
    url: () => yup.string().url('URL inválida'),
    multiselect: () => yup.array(),
    checkbox: () => yup.array(),
    file: () => yup.mixed(),
    image: () => yup.mixed(),
  };

  const schemaFactory = schemaMap[fieldType];

  return schemaFactory ? schemaFactory() : yup.string();
};

const applyRequiredValidation = (
  schema: yup.AnySchema,
  field: FormField,
): yup.AnySchema => {
  if (!field.is_required) {
    return schema;
  }

  const arrayTypes = ['multiselect', 'checkbox'];
  const mixedTypes = ['file', 'image'];

  if (arrayTypes.includes(field.type)) {
    return (schema as yup.ArraySchema<yup.AnySchema>).min(
      1,
      REQUIRED_FIELD_MESSAGE,
    );
  }

  if (mixedTypes.includes(field.type)) {
    return schema.required(REQUIRED_FIELD_MESSAGE);
  }

  return (schema as yup.StringSchema).required(REQUIRED_FIELD_MESSAGE);
};

const applyValidationRules = (
  schema: yup.AnySchema,
  field: FormField,
): yup.AnySchema => {
  const rules = field.validation_rules;
  if (!rules) {
    return schema;
  }

  let result = schema;
  const stringSchema = result as yup.StringSchema;

  if (rules.min !== undefined && 'min' in result) {
    result = stringSchema.min(
      rules.min as number,
      `Mínimo de ${rules.min} caracteres`,
    );
  }

  if (rules.max !== undefined && 'max' in result) {
    result = stringSchema.max(
      rules.max as number,
      `Máximo de ${rules.max} caracteres`,
    );
  }

  const typesWithBuiltInPattern = ['cpf', 'cnpj', 'cep'];
  const hasCustomPattern =
    rules.pattern && !typesWithBuiltInPattern.includes(field.type);

  if (hasCustomPattern) {
    result = (result as yup.StringSchema).matches(
      new RegExp(rules.pattern as string),
      (rules.patternMessage as string) || 'Formato inválido',
    );
  }

  return result;
};

const applyConfirmValidation = (
  schema: yup.AnySchema,
  field: FormField,
): yup.AnySchema => {
  if (!field.confirm_field) {
    return schema;
  }

  return (schema as yup.StringSchema).oneOf(
    [yup.ref(field.confirm_field)],
    'Os campos não coincidem',
  );
};

const getFieldValidation = (field: FormField): yup.AnySchema => {
  let schema = getBaseSchema(field.type);
  schema = applyRequiredValidation(schema, field);
  schema = applyValidationRules(schema, field);
  schema = applyConfirmValidation(schema, field);

  return schema;
};

export const buildValidationSchema = (
  fields: RenderableField[],
  visibleFields: Set<string>,
): yup.AnyObjectSchema => {
  const shape: Record<string, yup.AnySchema> = {};

  fields.forEach((field) => {
    if (visibleFields.has(field.name)) {
      shape[field.name] = getFieldValidation(field as FormField);
    }
  });

  return yup.object().shape(shape);
};
